import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useRecentActivity } from '../tareas/api';
import type { ActivityItem } from '../tareas/api';
import { useFaviconBadge } from './useFaviconBadge';

function relativeTime(input: string): string {
  const d = new Date(input);
  const diff = Date.now() - d.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'ahora';
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d`;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

export default function NotificationsBell() {
  const { profile } = useAuth();
  const { unreadCount, unread, read } = useRecentActivity(profile?.id);
  useFaviconBadge(unreadCount);
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const handleItemClick = (item: ActivityItem) => {
    setOpen(false);
    navigate(`/tareas/${item.taskId}`);
  };

  const renderItem = (item: ActivityItem) => (
    <button
      key={`${item.kind}-${item.taskId}`}
      type="button"
      onClick={() => handleItemClick(item)}
      style={{
        ...styles.item,
        background: item.unread ? '#fafbff' : 'transparent',
      }}
    >
      <span style={{ ...styles.dot, background: item.unread ? item.authorColor : '#cbd5e1' }} />
      <div style={styles.itemBody}>
        <div style={styles.itemTop}>
          <span style={{
            color: item.unread ? item.authorColor : '#64748b',
            fontWeight: 700,
          }}>
            #{item.authorName}
          </span>
          <span style={styles.itemTime}>{relativeTime(item.createdAt)}</span>
        </div>
        <div style={{
          ...styles.itemAsunto,
          color: item.unread ? '#0f172a' : '#475569',
          fontWeight: item.unread ? 600 : 500,
        }}>
          {item.taskAsunto}
        </div>
        {item.bodySnippet && (
          <div style={{
            ...styles.itemSnippet,
            color: item.unread ? '#475569' : '#94a3b8',
          }}>
            {item.bodySnippet}
          </div>
        )}
      </div>
    </button>
  );

  const isEmpty = unread.length === 0 && read.length === 0;

  return (
    <div ref={wrapRef} style={styles.wrap}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={unreadCount > 0 ? `${unreadCount} hilo${unreadCount === 1 ? '' : 's'} con actividad nueva` : 'Sin actividad nueva'}
        style={{
          ...styles.btn,
          background: open ? '#f1f5f9' : 'transparent',
        }}
      >
        <svg
          width="20" height="20" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ color: unreadCount > 0 ? '#1d4ed8' : '#64748b', display: 'block' }}
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unreadCount > 0 && <span style={styles.badge}>{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>

      {open && (
        <div style={styles.popover}>
          <div style={styles.popoverHeader}>
            <span>Actividad reciente</span>
            {unreadCount > 0 && <span style={styles.popoverHeaderCount}>{unreadCount}</span>}
          </div>

          <div style={styles.list}>
            {isEmpty ? (
              <div style={styles.empty}>
                Sin actividad nueva. Cuando alguien escriba en un hilo tuyo aparecerá aquí.
              </div>
            ) : (
              <>
                {unread.length > 0 && (
                  <>
                    <div style={styles.sectionHeader}>No leídas</div>
                    {unread.map(renderItem)}
                  </>
                )}
                {read.length > 0 && (
                  <>
                    <div style={styles.sectionHeader}>Leídas recientes</div>
                    {read.map(renderItem)}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  wrap: {
    position: 'relative',
  },
  btn: {
    border: 'none',
    cursor: 'pointer',
    padding: '6px 8px',
    borderRadius: 8,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontSize: 14,
    color: '#0f172a',
  },
  bell: {
    fontSize: 18,
    lineHeight: 1,
  },
  badge: {
    background: '#dc2626',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    padding: '1px 6px',
    borderRadius: 10,
    minWidth: 16,
    textAlign: 'center',
    lineHeight: '15px',
  },
  popover: {
    position: 'absolute',
    top: 'calc(100% + 8px)',
    left: 0,
    width: 380,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(15,23,42,0.12)',
    zIndex: 200,
    overflow: 'hidden',
    fontFamily: "'Söhne', 'Helvetica Neue', Helvetica, Arial, sans-serif",
  },
  popoverHeader: {
    padding: '12px 16px',
    borderBottom: '1px solid #f1f5f9',
    fontSize: 13,
    fontWeight: 700,
    color: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  popoverHeaderCount: {
    background: '#0f172a',
    color: '#fff',
    fontSize: 11,
    fontWeight: 700,
    padding: '2px 8px',
    borderRadius: 10,
  },
  list: {
    maxHeight: 480,
    overflowY: 'auto',
  },
  sectionHeader: {
    padding: '10px 14px 6px',
    fontSize: 11,
    fontWeight: 700,
    color: '#64748b',
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    background: '#f8fafc',
    borderBottom: '1px solid #f1f5f9',
  },
  empty: {
    padding: '24px 16px',
    color: '#94a3b8',
    fontSize: 13,
    textAlign: 'center',
    lineHeight: 1.5,
  },
  item: {
    width: '100%',
    display: 'flex',
    gap: 10,
    padding: '10px 14px',
    border: 'none',
    borderBottom: '1px solid #f1f5f9',
    cursor: 'pointer',
    textAlign: 'left',
    alignItems: 'flex-start',
  },
  dot: {
    width: 8,
    height: 8,
    minWidth: 8,
    borderRadius: 4,
    marginTop: 6,
  },
  itemBody: {
    flex: 1,
    minWidth: 0,
  },
  itemTop: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 2,
    fontSize: 12,
  },
  itemTime: {
    color: '#94a3b8',
    fontSize: 11,
  },
  itemAsunto: {
    fontSize: 13,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    marginBottom: 2,
  },
  itemSnippet: {
    fontSize: 12,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    lineHeight: 1.4,
  },
};
